<template>
  <v-app>
    <v-main class="d-flex" :style="mainStyle">
      <v-container class="d-flex flex-column justify-space-evenly" fluid>
        <v-sheet color="transparent">
          <v-sheet color="transparent">
            <div v-html="homeConfig.topText"></div>
          </v-sheet>
          <v-img
            v-if="assets.images.insuranceLogo.src"
            class="mx-auto"
            cover
            :max-width="getLogoWidth()"
            :src="assets.images.insuranceLogo.src"
          />
        </v-sheet>

        <v-sheet color="transparent">
          <div v-html="homeConfig.sloganHtml"></div>
        </v-sheet>

        <v-sheet class="px-6 d-flex flex-column align-center" color="transparent">
          <v-alert v-if="storeError" type="error" icon="$error" class="mb-6 text-center">
            {{ $t("homeView.storeNotFound") }}
          </v-alert>

          <template v-else>
            <app-button
              v-if="buttonVisible"
              v-for="(button, index) in homeConfig.buttons"
              :key="index"
              :block="button.block"
              :class="button.class"
              :color="button.color"
              :size="button.size"
              :variant="button.variant"
              :to="button.route ? { name: button.route.replace('/', '') } : undefined"
              :loading="button.action === 'activate' && (isLoading || isAdvancing)"
              @click="handleButtonClick(button)"
            >
              {{ button.label }}
            </app-button>
          </template>
        </v-sheet>

        <v-sheet
          v-if="bottomImageSrc"
          color="transparent"
          class="d-flex justify-center home-bottom-container"
        >
          <v-img class="home-bottom-image" :src="bottomImageSrc" max-width="100%" contain />
        </v-sheet>

        <v-sheet v-else class="d-flex justify-center bg-transparent">
          <v-img
            class="bikehero-logo"
            max-width="70px"
            :src="logoSrc"
            style="opacity: 1; filter: none"
          />
        </v-sheet>

        <v-sheet class="d-flex justify-center bg-transparent">
          <router-link class="privacy-policy-link text-caption" :to="{ name: 'privacyPolicy' }">
            {{ $t("homeView.privacyPolicyLink") }}
          </router-link>
        </v-sheet>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
  import { useBrandConfig } from "@/composables/useBrand";
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";
  import axios from "axios";
  import { computed, onMounted, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";

  interface HomeButton {
    label: string;
    block?: boolean;
    class?: string;
    color?: string;
    size?: string;
    variant?: string;
    route?: string;
    action?: string;
  }

  type ApiErrorBody = {
    status: number;
    detail?: string;
  };

  const props = defineProps<{
    insuranceCode?: string;
    storeToken?: string;
  }>();

  const router = useRouter();
  const store = useAppStore();
  const route = useRoute();
  const api = useApiStore();
  const buttonVisible = ref(true);

  const { assets, appTexts } = useBrandConfig();
  const { mainStyle } = usePageBackground();

  const homeConfig = computed(() => ({
    topText: appTexts.homeTopText,
    sloganHtml: appTexts.homeSloganHtml,
    buttons: (appTexts.homeButtons as HomeButton[]).filter((button) => {
      if (button.route === "/tutorial") {
        return (store.numberOfTutorialSteps ?? 0) > 0;
      }
      return true;
    }),
  }));

  const tag = route.query.tag as string | undefined;
  const storeToken = props.storeToken || (route.params.storeToken as string | undefined);
  const sellerContactId = route.query.c as string | undefined;

  const isLoading = ref(false);
  const { isAdvancing, advance: advanceFlow } = useStepFlow("/");

  function getNormalizedPath(): string {
    const homeRoutes = ["home", "storeHome", "homeWithTag", "insurance"];
    if (homeRoutes.includes(route.name as string)) {
      return "/";
    }
    return route.path;
  }

  async function advance(isClient?: boolean) {
    if (isClient !== undefined) {
      store.isClient = isClient;
    }

    if (!tag && !storeToken) {
      router.push({ path: "/client-info" });
      return;
    }

    try {
      await advanceFlow(async () => {
        await api.resolveTagEntry(tag, storeToken, sellerContactId);
      });
    } catch (error) {
      console.error("Error in advance:", error);
    }
  }

  function handleButtonClick(button: HomeButton) {
    if (button.action === "activate") {
      advance(true);
    } else if (button.action === "bikeshop") {
      advance(false);
    }
  }

  function getLogoWidth(): string {
    return assets.appTexts.logoWidth ? `${assets.appTexts.logoWidth}` : "300px";
  }

  const S3_BASE_URL = import.meta.env.VITE_APP_S3_PUBLIC_URL;
  const brand = useAppStore().brand;

  const logoSrc = computed(() => `${S3_BASE_URL}/${brand}/assets/powered-by-bkhr.svg`);

  const bottomImageSrc = computed(() => {
    const fileName = appTexts.bottomImage;
    if (!fileName) {
      return undefined;
    }

    return `${S3_BASE_URL}/${brand}/assets/${fileName}`;
  });

  const storeError = ref(false);

  function isPendingPricingError(error: unknown): boolean {
    return axios.isAxiosError<ApiErrorBody>(error) && error.response?.data?.status === 400;
  }

  function redirectOnInitError(error: unknown): void {
    if (tag && isPendingPricingError(error)) {
      router.push({ name: "pendingPricing" });
      return;
    }
    storeError.value = true;
  }

  onMounted(async () => {
    const hasJourneyInProgress = Boolean(api.insuranceCode);
    if (window.location.hostname.includes("adx.bikehero.com.br") && !hasJourneyInProgress) {
      buttonVisible.value = false;
    }
    if (storeToken || tag) {
      isLoading.value = true;
      try {
        await api.resolveTagEntry(tag, storeToken, sellerContactId);
        if (tag) {
          store.isClient = true;
          const nextScreenPath = await api.sequenceScreenFlow(getNormalizedPath(), true);
          router.push({ path: nextScreenPath });
        }
      } catch (error) {
        console.error("[HomeView] Error in auto-init:", error);
        redirectOnInitError(error);
      } finally {
        isLoading.value = false;
      }
    }
  });
</script>

<style lang="scss" scoped>
  .bikehero-logo {
    opacity: 0.8;
  }

  .home-bottom-image {
    margin-top: 16px;
    width: 100%;
  }

  .home-bottom-container {
    width: var(--frame-width, 100vw);
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: calc(var(--frame-width, 100vw) / -2);
    margin-right: calc(var(--frame-width, 100vw) / -2);
  }

  :deep(.v-main) {
    padding: 0 !important;
  }

  :deep(.v-main__wrap) {
    min-height: 100vh;
  }

  .privacy-policy-link {
    color: #ffffff;
    opacity: 0.7;
    text-decoration: underline;
  }
</style>
